import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Plus, 
  Trash2, 
  FileEdit,
  Palette,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';

const AppointmentReasons = () => {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newReason, setNewReason] = useState({ name: '', color: '#00d1ff', duration_minutes: 15 });
  const { addNotification } = useNotifications();

  const fetchReasons = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointment_reasons')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      setReasons(data || []);
    } catch (err) {
      addNotification('Error al cargar motivos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReasons();
  }, []);

  const handleSave = async () => {
    if (!newReason.name) {
      addNotification('El nombre es obligatorio', 'warning');
      return;
    }
    try {
      const { error } = await supabase
        .from('appointment_reasons')
        .insert([newReason]);
      if (error) throw error;
      addNotification('Motivo guardado correctamente', 'success');
      setNewReason({ name: '', color: '#00d1ff', duration_minutes: 15 });
      setShowForm(false);
      fetchReasons();
    } catch (err) {
      addNotification('Error al guardar: ' + err.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este motivo?')) return;
    try {
      const { error } = await supabase.from('appointment_reasons').delete().eq('id', id);
      if (error) throw error;
      addNotification('Motivo eliminado', 'info');
      fetchReasons();
    } catch (err) {
      addNotification('Error al eliminar', 'error');
    }
  };

  return (
    <div className="settings-section animate-fade">
      <div className="flex-between">
        <div>
          <h3>Motivos de Turno</h3>
          <p className="section-desc">Configurá las razones por las cuales tus pacientes solicitan una cita.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'CANCELAR' : '+ NUEVO MOTIVO'}
        </button>
      </div>

      {showForm && (
        <div className="reason-form-card card glass-panel-premium animate-slide-in" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
            <div className="form-group">
              <label>Nombre del Motivo</label>
              <input 
                type="text" 
                placeholder="Ej: Control, Urgencia..."
                value={newReason.name}
                onChange={(e) => setNewReason({...newReason, name: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Color Identificador</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={newReason.color}
                  onChange={(e) => setNewReason({...newReason, color: e.target.value})}
                  style={{ width: '42px', padding: '0', border: 'none', background: 'transparent' }}
                />
                <input 
                  type="text" 
                  value={newReason.color}
                  onChange={(e) => setNewReason({...newReason, color: e.target.value})}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Duración (Minutos)</label>
              <select 
                value={newReason.duration_minutes}
                onChange={(e) => setNewReason({...newReason, duration_minutes: parseInt(e.target.value)})}
              >
                <option value={15}>15 min</option>
                <option value={20}>20 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
              </select>
            </div>
            <button className="btn-primary" onClick={handleSave} style={{ height: '42px' }}>
              GUARDAR
            </button>
          </div>
        </div>
      )}

      <div className="reasons-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
        {loading ? (
          <div className="text-center-p">Cargando motivos...</div>
        ) : reasons.map(r => (
          <div key={r.id} className="reason-item card glass-panel" style={{ padding: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: `4px solid ${r.color}` }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: '700', color: 'white' }}>{r.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Clock size={12} /> {r.duration_minutes} min
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn-report-action docs" title="Editar"><FileEdit size={14} /></button>
              <button className="btn-report-action delete" onClick={() => handleDelete(r.id)} title="Eliminar"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppointmentReasons;
